import React, {useEffect, useState, useMemo, useCallback} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import 'quill-emoji';
import * as css from './TextEditor.css.js';
import {SnowTheme} from 'quill-color-picker-enhance';
import 'quill-color-picker-enhance/dist/index.css';
import {addNoDelete, addVariables, convertVariablesToHtml} from './Variables.js';
import {useVariables} from '../../assets/objects/Variables.js';
import QuillVariablesDropdown from './QuillVariablesDropdown.tsx';
import {useTranslation} from 'react-i18next';
import DOMPurify from 'dompurify';
import {SMS_CHARS_ON_MESSAGE, SMS_FREE_CHARS} from '../../pages/branding/smsEditor/SmsEditor.js';
import {DEFAULT_LANG} from '../../globalVariables/locals.js';
import {decodeHtmlEntities, clearText, convertTextBeforeSave, hasDisabledVariables, SANITIZE_CONFIG, restoreImageStyles} from './TextEditorHelpers.js';
import {handlePaste as handlePasteUtil, handleCopy as handleCopyUtil, createImageHandler} from './TextEditorHandlers.js';
import {getModulesConfig} from './TextEditorModules.js';
import ImageEditor from './ImageEditor.js';
import useShow from '../../hooks/useShow';

function TextEditor({defaultText, onSave, onChange, showToolbar = false, limit={}, quillRef, dependencies = [], lang=DEFAULT_LANG, prefix=null, suffix=null, variablesType='', expanded = false, minHeight = null, placeholder = '', disableVariables = false, disable = {}, variablesDataCy = undefined}) {
    const Quill = ReactQuill.Quill;
    Quill.register('themes/snow-quill-color-picker-enhance', SnowTheme);
    const {t} = useTranslation('variables');
    const [text, setText] = useState('');
    const {defaultVariables, disabledVariables} = useVariables(disableVariables ? null : variablesType);
    const [showWarning, setShowWarning] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const variables = disableVariables ? {} : {...defaultVariables, ...disabledVariables};
    const {showCharCounter,maxChar,showMessagesAmount}=limit;
    const direction=lang!=='he'?'ltr':'rtl';
    const showImageEditor = useShow();

    const handleSetSelectedImage = (image) => {
        setSelectedImage(image);
        if(image && quillRef.current) {
            showImageEditor.open();
        } else {
            showImageEditor.close();
        }
    };

    const saveText = () => {
        if(quillRef.current) {
            onSave(convertTextBeforeSave(quillRef));
        }
    };

    useEffect(() => {
        if(quillRef.current ) {
            if(!disableVariables) {
                addVariables();
                addNoDelete();
            }
            const quill = quillRef.current.getEditor();
            let htmlWithValues = '';
            if(defaultText) {
                if(!disableVariables) {
                    htmlWithValues = convertVariablesToHtml(defaultText, variables, t);
                } else {
                    htmlWithValues = defaultText;
                }
                htmlWithValues = htmlWithValues.replace(/<p><\/p>/g, '<p><br></p>');
            }
            const sanitizedHtml = DOMPurify.sanitize(htmlWithValues, SANITIZE_CONFIG);
            quill.setContents(quill.clipboard.convert(sanitizedHtml));

            // Restore image styles after content is loaded
            if(defaultText) {
                let attempts = 0;
                const checkAndRestore = () => {
                    const images = quill.root.querySelectorAll('img');
                    const hasImagesWithSize = images.length > 0 && Array.from(images).some(img =>
                        img.src && (img.offsetWidth > 0 || img.offsetHeight > 0)
                    );

                    if(hasImagesWithSize) {
                        restoreImageStyles(quill, defaultText);
                    } else if(++attempts < 10) {
                        requestAnimationFrame(checkAndRestore);
                    }
                };
                requestAnimationFrame(checkAndRestore);
            }
        }
    }, dependencies);

    const imageHandler = useCallback(createImageHandler(quillRef), [quillRef]);

    const handlePaste = useCallback((e) => {
        handlePasteUtil(e, quillRef, maxChar);
    }, [quillRef, maxChar]);

    const handleCopy = useCallback((e) => {
        handleCopyUtil(e, quillRef);
    }, [quillRef]);

    const checkForDisabledVariables = () => {
        if(disableVariables) {
            setShowWarning(false);
            return;
        }
        const disabled = hasDisabledVariables(quillRef, disabledVariables);
        setShowWarning(disabled);
    };

    // Ensure all links open in new tab and handle image clicks
    const handleLinkClick = (e) => {
        const img = e.target.closest('img');
        if(img) {
            // Don't handle images inside variables
            if(img.closest('.ql-variable')) return;
            img.style.cursor = 'pointer';
            e.preventDefault();
            e.stopPropagation();
            handleSetSelectedImage(img);
            return;
        }

        const link = e.target.closest('a');
        if(link && link.href) {
            e.preventDefault();
            window.open(link.href, '_blank', 'noopener,noreferrer');
        }
    };

    // Setup color picker fix and event listeners
    useEffect(() => {
        if(!quillRef?.current) return;
        const quill = quillRef.current.getEditor();

        // Setup color picker fix (only once)
        const toolbarElement = quill.getModule('toolbar').container;
        if(!toolbarElement.querySelector('.custom-picker.color')) {
            const createAndAppendInput = (className) => {
                const input = document.createElement('input');
                input.type = 'color';
                input.hidden = true;
                input.className = className;
                toolbarElement.appendChild(input);
            };
            createAndAppendInput('custom-picker color');
            createAndAppendInput('custom-picker background');
        }
        // Attach listeners
        if(!disableVariables) {
            quill.on('text-change', checkForDisabledVariables);
        }
        quill.on('blur', saveText);
        quill.root.addEventListener('blur', saveText);
        quill.root.addEventListener('paste', handlePaste);
        quill.root.addEventListener('copy', handleCopy);
        quill.root.addEventListener('click', handleLinkClick);

        // Initial setup
        if(!disableVariables) {
            checkForDisabledVariables();
        }

        return () => {
            if(!disableVariables) {
                quill.off('text-change', checkForDisabledVariables);
            }
            quill.off('blur', saveText);
            quill.root.removeEventListener('blur', saveText);
            quill.root.removeEventListener('paste', handlePaste);
            quill.root.removeEventListener('copy', handleCopy);
            quill.root.removeEventListener('click', handleLinkClick);
        };
    }, [handlePaste, handleCopy, saveText, disabledVariables, handleSetSelectedImage, disableVariables]);

    const modules = useMemo(() => getModulesConfig(imageHandler, expanded, disable), [imageHandler, expanded, disable]);

    const handleTextChange = (value) => {
        const clearValue = clearText(value);
        if(onChange && value){
            onChange(decodeHtmlEntities(convertTextBeforeSave(quillRef)));
        }
        setText(clearValue);
    };

    const isMaxLengthReached = maxChar ? text.length >= maxChar : false;

    const handleKeyDown = (event) => {
        if(isMaxLengthReached && event.key !== 'Backspace') {
            event.preventDefault();
        }
    };

    return (
        <css.wrap direction={direction} showToolbar={showToolbar} showCharCounter={showCharCounter} $minHeight={minHeight}>
            {!disableVariables && <QuillVariablesDropdown direction={direction} quillRef={quillRef} remainingChars={maxChar - text.length} variablesType={variablesType} dataCy={variablesDataCy}/>}
            {!!prefix && prefix}
            <ReactQuill
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                ref={quillRef}
                modules={modules}
                theme='snow-quill-color-picker-enhance'
                style={minHeight ? {minHeight} : {}}
                placeholder={placeholder}
            />
            {!!suffix && suffix}
            {showCharCounter&&<css.maxChar>
                {`${text.length}/${maxChar} `}
                <b>{t('chars')}   </b>
                {(showMessagesAmount&& text.length>SMS_FREE_CHARS)&&
                <>{`${t('messagesCount')}:${(Math.ceil((text.length-SMS_FREE_CHARS) / SMS_CHARS_ON_MESSAGE))} `}
                    <span>{t('additionalCost')}</span>
                </>}
            </css.maxChar>}
            {showWarning && <css.warning>{t('warningDisabled')}</css.warning>}
            {showImageEditor.isOpen &&
                <ImageEditor
                    showImageEditor={showImageEditor}
                    imageElement={selectedImage}
                    quill={quillRef.current.getEditor()}
                    onClose={() => { handleSetSelectedImage(null); saveText(); }}
                />
            }
        </css.wrap>
    );
}

export default TextEditor;
